import {Icon} from '@wandb/weave/components/Icon';
import * as Tabs from '@wandb/weave/components/Tabs';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';

import {
  useBatchRegistryViewerMembershipQuery,
  useFetchProjectTriggersQuery,
} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
import {AutomationsHistoryTab} from '../../Launch/Triggers/AutomationsHistoryTab';
import {AutomationsTab} from '../../Launch/Triggers/AutomationsTab';
import {CreateTriggerDrawer} from '../../Launch/Triggers/CreateTriggerDrawer/CreateTriggerDrawer';
import {BaseTriggerDrawerInit} from '../../Launch/Triggers/CreateTriggerDrawer/EventStore';
import {EntryPoint} from '../../Launch/Triggers/CreateTriggerDrawer/types';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {CollectionsTab} from '../common/CollectionsTab';
import {useIsProjectReadOnly} from '../common/hooks';
import {useRegistryAccountSettings} from '../common/RegistryAccountSettings';
import {GQLRegistryProjectViewerMembershipType as RegistryProjectType} from '../common/types';
import {globalRegistryType} from '../common/utils';
import {getViewerRegistryMembershipData} from '../RegistriesHomepage/utils';
import {CreateRegisteredCollectionDrawer} from '../workflows/CreateRegisteredCollectionDrawer/CreateRegisteredCollectionDrawer';
import {
  useAllSingleRegistryArtifactCollectionsWithoutMembers,
  useSearchablePaginatedSingleRegistryArtifactCollections,
} from './hooks';
import {RegistryPageHeader} from './RegistryPageHeader';
import {SDKVersionProvider} from './SDKVersionContext';
import {AccessTeamModelRegistryModal} from './TeamModelRegistry/AccessTeamModelRegistryModal';

const REGISTRY_PAGE_SIZE = 10;

type RegistryPageProps = {
  registry: globalRegistryType;
};

export const COLLECTIONS_TAB_NAME = 'collections';
export const AUTOMATIONS_TAB_NAME = 'automations';
export const AUTOMATIONS_HISTORY_TAB_NAME = 'automations-history';

export const RegistryPage = ({registry}: RegistryPageProps) => {
  const {
    entityName,
    registryName,
    registryProjectName,
    registryProjectID,
    allowAllArtifactTypesInRegistry,
    orgName,
  } = registry;

  const viewer = useViewer();

  const [selectedTab, setSelectedTab] = useState(COLLECTIONS_TAB_NAME);

  const [showNewCollectionModal, setShowNewCollectionModal] = useState(false);
  const [showNewAutomationModal, setShowNewAutomationModal] = useState(false);
  const [showTeamRegistryModal, setShowTeamRegistryModal] = useState(false);

  // FIXME: Suboptimal workaround to preserve behavior of CreateRegisteredCollectionDrawer,
  // which expects a list of existing collections to check for conflicts
  const {
    collections: existingCollections,
    refetch: refetchExistingCollections,
  } = useAllSingleRegistryArtifactCollectionsWithoutMembers({
    entityName,
    projectName: registryProjectName,
  });

  const {
    currentPage: currentPaginatedCollections,
    loading: collectionsLoading,
    refetch: refetchCollections,
    hasNextPage,
    totalCollectionsLoaded,
    page,
    onPageChange,
  } = useSearchablePaginatedSingleRegistryArtifactCollections({
    entityName,
    projectName: registryProjectName,
    pageSize: REGISTRY_PAGE_SIZE,
  });

  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName: registryProjectName,
      entityName,
      projectOnly: true,
    },
    context: propagateErrorsContext(),
  });
  const baseTriggerDrawerInit: BaseTriggerDrawerInit = {
    entryPoint: EntryPoint.REGISTRY,
    eventEntityName: entityName,
    eventProjectID: registryProjectID,
    eventProjectName: registryProjectName,
    artifactCollectionID: undefined,
    artifactCollectionName: undefined,
    existingAutomations: [],
    actionEntityName: undefined,
    suggestedMetricName: undefined,
    defaultEvent: undefined,
    defaultAction: undefined,
    panelLink: undefined,
  };

  const {isProjectReadOnly: readOnly} = useIsProjectReadOnly(
    entityName,
    registryProjectName
  );

  const {settingsLoading, viewerMemberRole} = useRegistryAccountSettings();

  const {
    data: batchRegViewerMembershipData,
    loading: batchRegViewerMembershipLoading,
  } = useBatchRegistryViewerMembershipQuery({
    variables: {
      entityName: orgName,
      projectIDs: [registryProjectID],
      viewerID: viewer?.id ?? '',
    },
    context: propagateErrorsContext(),
    skip: !registryProjectID || !viewer?.id,
    fetchPolicy: 'network-only',
  });

  if (
    settingsLoading ||
    batchRegViewerMembershipLoading ||
    !batchRegViewerMembershipData?.projects?.edges?.[0]?.node
  ) {
    return <Loader name="global-registry-landing-page" size="huge" />;
  }

  const singleRegistryProject = batchRegViewerMembershipData?.projects
    ?.edges?.[0]?.node as RegistryProjectType;

  const {isAvailableToJoin} = getViewerRegistryMembershipData({
    registryAccess: registry.access,
    viewerMemberRole,
    registry: singleRegistryProject,
  });

  return (
    <Tailwind style={{height: '100%', width: '100%'}}>
      <div className="flex h-full w-full flex-col bg-moon-50">
        <SDKVersionProvider>
          <RegistryPageHeader
            registry={registry}
            onCreateCollection={() => {
              setShowNewCollectionModal(true);
            }}
            onCreateAutomation={() => {
              setShowNewAutomationModal(true);
            }}
            showJoinBanner={isAvailableToJoin}
          />
          {/*  TODO: Remove this banner after the model registry migration is complete */}
          {registryName === 'model' && (
            <Banner variant="warning" className="mb-20">
              <BannerFlexWrapper>
                <div>
                  <Icon name="warning" />
                </div>
                <BannerTextWrapper className="px-16">
                  <span>
                    <BannerBoldText>
                      Looking for your old team-level model registry?
                    </BannerBoldText>{' '}
                    You can still access it here.
                  </span>
                </BannerTextWrapper>
              </BannerFlexWrapper>
              <div
                className="cursor-pointer font-semibold"
                onClick={() => setShowTeamRegistryModal(true)}>
                Visit the old model registry
              </div>
            </Banner>
          )}
          {/* If registry is available to join, but viewer has not joined yet, do not show registry actions */}
          {!isAvailableToJoin && (
            <>
              <Tabs.Root
                value={selectedTab}
                onValueChange={value => setSelectedTab(value)}>
                <Tabs.List>
                  <Tabs.Trigger value={COLLECTIONS_TAB_NAME}>
                    Collections
                  </Tabs.Trigger>
                  <Tabs.Trigger value={AUTOMATIONS_TAB_NAME}>
                    Automations
                  </Tabs.Trigger>
                  <Tabs.Trigger value={AUTOMATIONS_HISTORY_TAB_NAME}>
                    Automation History
                  </Tabs.Trigger>
                </Tabs.List>
                <div className="pt-16">
                  <Tabs.Content value={COLLECTIONS_TAB_NAME}>
                    <CollectionsTab
                      totalCount={registry.collectionCount}
                      currentPaginatedCollections={currentPaginatedCollections}
                      refetchCollections={refetchCollections}
                      setShowNewCollectionModal={setShowNewCollectionModal}
                      collectionsLoading={collectionsLoading}
                      totalCollectionsLoaded={totalCollectionsLoaded}
                      page={page}
                      pageSize={REGISTRY_PAGE_SIZE}
                      hasNextPage={hasNextPage}
                      onPageChange={onPageChange}
                      registry={registry}
                    />
                  </Tabs.Content>
                  <Tabs.Content value={AUTOMATIONS_TAB_NAME}>
                    <AutomationsTab
                      entityName={entityName}
                      projectName={registryProjectName}
                      baseTriggerDrawerInit={baseTriggerDrawerInit}
                      readOnly={readOnly}
                      projectOnly={true}
                    />
                  </Tabs.Content>
                  <Tabs.Content value={AUTOMATIONS_HISTORY_TAB_NAME}>
                    <AutomationsHistoryTab
                      entityName={entityName}
                      projectName={registryProjectName}
                    />
                  </Tabs.Content>
                </div>
              </Tabs.Root>
            </>
          )}
        </SDKVersionProvider>
      </div>
      <CreateRegisteredCollectionDrawer
        open={showNewCollectionModal}
        closeDrawer={() => {
          setShowNewCollectionModal(false);
        }}
        registryEntityName={entityName}
        defaultProjectName={registryProjectName}
        existingCollections={existingCollections}
        registryAllowsAllArtifactTypes={allowAllArtifactTypesInRegistry}
        refetchCollections={refetchCollections}
      />
      <CreateTriggerDrawer
        open={showNewAutomationModal}
        closeDrawer={() => {
          setShowNewAutomationModal(false);
        }}
        createTriggerDrawerInit={{
          ...baseTriggerDrawerInit,
          isUpdating: false,
          trigger: undefined,
        }}
        refetchProjectTriggers={projectTriggersQuery.refetch}
      />
      <AccessTeamModelRegistryModal
        isOpen={showTeamRegistryModal}
        setIsModalOpen={setShowTeamRegistryModal}
      />
    </Tailwind>
  );
};
